import { bootstrapApplication } from '@angular/platform-browser';
import {
    RouteReuseStrategy,
    provideRouter,
    withPreloading,
    PreloadAllModules,
    withComponentInputBinding, Router
} from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { AppComponent } from '@app/app.component';
import { environment } from '@env/environment';
import { enableProdMode, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import {
    BarcodeScannerService,
    HealthService,
    InitializeAppService,
    LoadingService, NetworkService, SqliteService,
    StorageService
} from '@app/services';
import { setNonce } from '@ionic/core/loader';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { httpInterceptors } from '@app/interceptors';
import { routes } from '@app/app.routes';
import { defineCustomElements as pwaElements } from '@ionic/pwa-elements/loader';
import { defineCustomElements as jeepSqlite } from 'jeep-sqlite/loader';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';

setNonce('d06799edb8b5cd8e877026071313b7b96adb99b1f117e670edee30e0b3231044');

if (environment.production)
{
    enableProdMode();
}

if (!Capacitor.isNativePlatform())
{
    pwaElements(window);

    jeepSqlite(window);

    window.addEventListener('DOMContentLoaded', async () =>
    {
        const jeepEl = document.createElement('jeep-sqlite');

        // Set the nonce attribute on the dynamically created element
        jeepEl.setAttribute('nonce', 'd06799edb8b5cd8e877026071313b7b96adb99b1f117e670edee30e0b3231044');

        document.body.appendChild(jeepEl);
        await customElements.whenDefined('jeep-sqlite');
        jeepEl.autoSave = true;
    });
}

if (environment.sentry.enabled)
{
    Sentry.init(
        {
            enabled: environment.sentry.enabled,
            dsn: environment.sentry.dsn,
            // Adds request headers and IP for users, for more info visit:
            // https://docs.sentry.io/platforms/javascript/guides/capacitor/configuration/options/#sendDefaultPii
            sendDefaultPii: true,
            debug: !environment.production,
            environment: environment.production ? 'production' : 'development',
            // Set your release version, such as 'getsentry@1.0.0'
            release: `${environment.sentry.appName}@${environment.version}`,
            // Set your dist version, such as '1'
            dist: environment.version,
            // Logs requires @sentry/capacitor 2.0.0 or newer.
            enableLogs: true,
            integrations: [
                // Registers and configures the Tracing integration,
                // which automatically instruments your application to monitor its
                // performance, including custom Angular routing instrumentation
                Sentry.browserTracingIntegration(),
                // Registers the Replay integration,
                // which automatically captures Session Replays
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    blockAllMedia: false,
                }),
            ],
            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for tracing.
            // We recommend adjusting this value in production
            // Learn more at
            // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
            tracesSampleRate: 0.5,
            // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [
                'localhost',
                environment.domain,
                environment.reverb.authEndpoint,
                environment.reverb.wsHost,
                // /^https:\/\/yourserver\.io\/api/, example
            ],
            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            // Learn more at
            // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
            replaysSessionSampleRate: 0.03,
            replaysOnErrorSampleRate: 0.8,
        },
        SentryAngular.init,
    );
}

bootstrapApplication(AppComponent, {
    providers: [
        NetworkService,
        StorageService,
        SqliteService,
        BarcodeScannerService,
        LoadingService,
        HealthService,
        InitializeAppService,
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        {
            provide: ErrorHandler,
            useValue: SentryAngular.createErrorHandler(),
        },
        {
            provide: SentryAngular.TraceService,
            deps: [Router],
        },
        provideIonicAngular({
            innerHTMLTemplatesEnabled: true,
            swipeBackEnabled: false,
            animated: true,
            hardwareBackButton: true,
            rippleEffect: true,
            useSetInputAPI: true, //  required for input signals on controller based modals.
        }),
        provideHttpClient(withInterceptors(httpInterceptors)),
        provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
        provideAppInitializer(() =>
        {
            const initializeAppService = inject(InitializeAppService);

            return initializeAppService.initializeApp();
        }),
    ],
});
